import React, { useState } from 'react';
import { Alert, AlertTitle, Button, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';

/**
 * PastDueWarningBanner - Shows warning for account owners when subscription is past_due
 *
 * Features:
 * - Sticky banner at top of page
 * - Dismissible (but reappears on page reload)
 * - Direct link to billing page
 * - Warning message about payment failure
 */
const PastDueWarningBanner = ({ subscriptionStatus, userRole }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Only show for owners and past_due status
  if (userRole !== 'owner' || subscriptionStatus !== 'past_due' || dismissed) {
    return null;
  }

  const handleUpdatePayment = () => {
    navigate('/settings/organization?tab=billing');
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1200,
        width: '100%'
      }}
    >
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={handleUpdatePayment}
              sx={{
                fontWeight: 600,
                borderColor: 'rgba(0, 0, 0, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.5)',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              Update Payment Method
            </Button>
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => setDismissed(true)}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        }
        sx={{
          borderRadius: 0,
          '& .MuiAlert-message': {
            width: '100%'
          }
        }}
      >
        <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
          Payment Past Due
        </AlertTitle>
        Your subscription payment has failed. Please update your payment method to avoid service interruption.
        Your team can continue using the service while you resolve this issue.
      </Alert>
    </Box>
  );
};

export default PastDueWarningBanner;
